import React, { useState, ChangeEvent } from "react";
import {
  Question,
  ElementFactory,
  Serializer,
} from "survey-core";
import {
  ReactQuestionFactory,
  SurveyQuestionElementBase,
} from "survey-react-ui";
import { typeFormatters } from "../Formatters";
import { AddressSearchService } from "../../../landers/Utils/services/AddressSearch.service";

const CUSTOM_TYPE = "postcode";

export class QuestionPostcodeModel extends Question {
  getType() {
    return CUSTOM_TYPE;
  }

  get postcodeLookup() {
    return this.getPropertyValue("postcodeLookup");
  }
  set postcodeLookup(val) {
    this.setPropertyValue("postcodeLookup", val);
  }

  get addressFieldName() {
    return this.getPropertyValue("addressFieldName", "address");
  }
  set addressFieldName(val) {
    this.setPropertyValue("addressFieldName", val);
  }

  get cityFieldName() {
    return this.getPropertyValue("cityFieldName", "city");
  }
  set cityFieldName(val) {
    this.setPropertyValue("cityFieldName", val);
  }
}

// Add question type metadata for further serialization into JSON
Serializer.addClass(
  CUSTOM_TYPE,
  [
    {
      name: "postcodeLookup:boolean",
      default: true,
    },
    {
      name: "addressFieldName:string",
      default: "address",
    },
    {
      name: "cityFieldName:string",
      default: "city",
    },
  ],
  function () {
    return new QuestionPostcodeModel("");
  },
  "question"
);

ElementFactory.Instance.registerElement(CUSTOM_TYPE, (name) => {
  return new QuestionPostcodeModel(name);
});

interface PostcodeState {
  value: string;
  suggestions: any[];
  postcodeResults: string[];
}

// A class that renders questions of the new type in the UI
export class SurveyQuestionPostcode extends SurveyQuestionElementBase {

  constructor(props: any) {
    super(props);
    this.state = {
      value: this.question.value,
      suggestions: [],
      postcodeResults: [],
    };


    // Bind methods to preserve this context
    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.postcodeSelected = this.postcodeSelected.bind(this);
  }

  componentDidUpdate(prevProps: any) {
    const currentQuestionValue = this.question.value;
  
    if (currentQuestionValue !== this.state.value) {
      this.setState({ value: currentQuestionValue });
    }
  }

  get question() {
    return this.questionBase as QuestionPostcodeModel;
  }

  get value() {
    return this.question.value;
  }


  private debounceTimer: NodeJS.Timeout | null = null;

  async handleChange(event: ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    this.question.value = value;
    this.setState({ value });

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    if (value.length >= 2) {
      this.debounceTimer = setTimeout(async () => {
        try {
          const suggestions = await AddressSearchService.getPostcodeSuggestions(value);
          this.setState({ postcodeResults: suggestions });
        } catch (error) {
          console.error("Autocomplete failed", error);
        }
      }, 100);
    } else {
      this.setState({ postcodeResults: [] });
    }
  }

  handleBlur() {
    const formattedValue = this.formatPostcodeValue(this.value);
    this.question.value = formattedValue;
    this.setState({ value: formattedValue });
  }

  postcodeSelected(item: string) {
    const formattedValue = this.formatPostcodeValue(item);
    this.question.value = formattedValue;
    this.setState({
      value: formattedValue,
      postcodeResults: [],
    });

    AddressSearchService.getAddressesForPostcode(item);
  }

  formatPostcodeValue(postcode: string) {
    return typeFormatters.postcode(postcode);
  }

  renderElement() {
    if (this.question.isReadOnly || this.isDisplayMode) {
      return <div>{this.value}</div>;
    }

    return (
      <div className="sd-question__content">
        <div className="sd-input">
          <div className="sd-input__control">
            <input
              className="sd-input sd-text"
              type="text"
              value={this.state.value || ""}
              onChange={this.handleChange}
              onBlur={this.handleBlur}
              placeholder={this.question.placeholder}
            />
            {this.state.postcodeResults.length > 0 && (
              <div className="sd-selectbase mt-5 postcode-results-container">
                <div className="text-muted">Please select a postcode:</div>

                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {this.state.postcodeResults.map(
                    (item: string, index: number) => (
                      <button
                        onClick={() => this.postcodeSelected(item)}
                        className="btn btn-primary m-1"
                        key={index}
                        style={{ flex: "0 0 auto" }}
                      >
                        {item}
                      </button>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

// Register `SurveyQuestionPostcode` as a class that renders `postcode` questions
ReactQuestionFactory.Instance.registerQuestion(CUSTOM_TYPE, (props) => {
  return React.createElement(SurveyQuestionPostcode, props);
});
